import { GENERAL } from "./Config";
import { normaliseAngle, shortestAngle } from "./Sailing";

/**
 * The weather over the arena: one direction, one constant strength, wandering slowly.
 *
 * The wind is a **directional force**. It has a bearing that changes and a magnitude that does
 * not — see design/Wind.md for why the strength is held still for now. What a hull does with it
 * is `sailPower` below, which [Sailing.ts](Sailing.ts) turns into a speed: the whole of the
 * point-of-sail curve is one cosine and one dial.
 *
 * Like `shared/Sailing.ts`, this is imported by both ends. The **arena owns the wander** — it
 * is the only thing allowed to roll dice about the sea — but the constants and the convention
 * live here so a client drawing the vane and a server veering it cannot disagree about what the
 * number on the wire means.
 *
 * The numbers marked *tunable* come from `config/general.json` by way of [Config.ts](Config.ts)
 * and are edited in the dashboard (see design/Settings.md). They are read once at module load.
 *
 * **The convention, and the thing to get right:** a wind direction is a heading like any other
 * in this game — radians, 0 = north, growing clockwise, normalised to (-PI, PI] — and it names
 * the direction the wind is blowing **toward**, not the quarter it comes from.
 *
 * That is the opposite of how a sailor names a wind (a "northerly" blows *from* the north), and
 * it is chosen deliberately: every other angle in the repo is a direction of travel, and a
 * sailing system has to compare the wind against a hull's `heading`. One convention with a
 * surprising name beats two conventions with obvious ones. What the player is shown is an arrow,
 * which has no such ambiguity — see `src/scripts/ui/WindVane.ts`.
 */

/**
 * How hard the wind blows, in pixels per second. **Tunable, and constant** — the whole of the
 * force's magnitude, held still on purpose so that the only thing a captain has to read is the
 * arrow. Compare it against CRUISE_SPEED: that is the ratio the dashboard shows, and the one a
 * sail will eventually be worth some fraction of.
 */
export const WIND_SPEED = GENERAL.windSpeed;

/**
 * How fast the wind swings toward the quarter it is chasing, in radians per second. **Tunable**,
 * and authored in degrees — converted once, here, because the dashboard's slider is the only
 * place a human reads it and nobody has an intuition for 0.05 rad/s.
 */
export const WIND_VEER_RATE = (GENERAL.windVeerRate * Math.PI) / 180;

/**
 * How close to the eye of the wind a hull may point before its canvas draws nothing, in radians,
 * measured from dead upwind. **Tunable**, authored in degrees for the same reason the veer rate
 * is. The dead sector is twice this wide.
 */
export const WIND_NO_GO = (GENERAL.windNoGoAngle * Math.PI) / 180;

/**
 * The angle off dead-downwind at which drive reaches zero — the closest to the wind a hull can
 * still sail. Its cosine is the only thing `sailPower` actually needs.
 */
const SAIL_LIMIT_COS = Math.cos(Math.PI - WIND_NO_GO);

/**
 * The share of a hull's cruise speed that does **not** come from the wind, in pixels per second:
 * what it makes under bare poles, with the canvas doing nothing at all.
 *
 * `boatBaseSpeed - windSpeed` by construction, so a rung-1 hull running dead downwind makes
 * exactly the cruise speed the arena had before any of this existed, and every number tuned
 * against that one still means what it meant. `windSpeed` is therefore the single dial for *how
 * much the wind matters*: at zero the whole system switches off.
 *
 * Floored at BARE_SHARE_FLOOR of cruise, because `windSpeed` goes to 400 in the dashboard and a
 * hull that can be stopped dead by pointing it the wrong way is a hull that cannot get itself out
 * of trouble. **A hull is never stopped** — see design/Wind.md.
 *
 * The cruise speed is read off `GENERAL` here rather than imported as `CRUISE_SPEED` from
 * [Sailing.ts](Sailing.ts) **on purpose**: that would be a module-evaluation-time read across the
 * import cycle between these two files, and the pair would then only load in one particular
 * order. Everything this module uses from Sailing.ts is called rather than read, which is what
 * keeps the cycle harmless. Do not add a top-level `const` here that reads one of its exports.
 */
const BARE_SHARE_FLOOR = 0.15;
export const BARE_SPEED = Math.max(GENERAL.boatBaseSpeed * BARE_SHARE_FLOOR, GENERAL.boatBaseSpeed - WIND_SPEED);

/**
 * How much of the wind a hull pointed at `heading` collects, from nothing to all of it.
 *
 * `theta` is the angle between the way the wind is blowing and the way the ship is pointing, so
 * zero is running dead downwind and +/-PI is straight into the eye of it:
 *
 * ```
 * power = max(0, (cos theta - cos limit) / (1 - cos limit))        limit = PI - WIND_NO_GO
 * ```
 *
 * One at zero, zero at the limit and beyond, and falling off between — a cosine, which is the
 * projection of the wind onto the hull's own axis, shifted so the last stretch before the eye of
 * the wind falls to nothing rather than merely getting small. **The no-go sector is not a second
 * rule with a branch of its own; it is where this one line reaches zero.**
 *
 * What it does *not* do is refuse a heading. A captain who points into the wind gets to, and gets
 * to be slow — the helm is a promise and this is not allowed to break it (design/Controls.md).
 *
 * Both ends call it, off the same `Ship.heading` and the same `ArenaState.wind`, so a client and
 * the arena cannot disagree about how fast a hull is going.
 */
export function sailPower(wind: number, heading: number): number {
    return Math.max(0, (Math.cos(shortestAngle(wind, heading)) - SAIL_LIMIT_COS) / (1 - SAIL_LIMIT_COS));
}

/** Mean seconds between the wind choosing a new quarter to chase. **Tunable.** */
export const WIND_SHIFT_INTERVAL = GENERAL.windShiftInterval;

/**
 * How much the gap between two shifts is jittered, as a fraction of WIND_SHIFT_INTERVAL: a
 * gap is uniform in `interval * [1 - jitter, 1 + jitter]`.
 *
 * A *decision*, not a dial (design/Settings.md). What somebody wants to drag is how often the
 * wind changes its mind; that it does not do so on a beat is not a tuning question, it is what
 * stops a whole arena of players learning to count seconds between shifts.
 */
export const WIND_SHIFT_JITTER = 0.5;

/**
 * Swings `wind` toward `target` at WIND_VEER_RATE and returns the new direction, normalised.
 *
 * Clamped to the remaining angle, so the wind settles on a quarter instead of overshooting it
 * and hunting back and forth — the same shape as `turnToward` in [Sailing.ts](Sailing.ts), with
 * its own rate. It takes the shortest way round, so a wind at north-west chasing north-east
 * veers through north rather than the long way about.
 */
export function veerWind(wind: number, target: number, deltaSeconds: number): number {
    const delta = shortestAngle(wind, target);
    return normaliseAngle(wind + Math.sign(delta) * Math.min(Math.abs(delta), WIND_VEER_RATE * deltaSeconds));
}

/**
 * A fresh quarter for the wind to chase: anywhere on the compass, uniformly.
 *
 * Uniform rather than a nudge off the current direction, and it is the **veer rate** that makes
 * the change gradual rather than the size of the step. At the default rate a target on the far
 * side of the compass takes a minute to reach and a new one is picked long before then, so the
 * wind is always partway toward somewhere — which is a sea that wanders, rather than one that
 * swings and then sits still.
 *
 * `random` is a parameter so the arena's dice are the only ones in the sim and a test can hand
 * this a sequence it chose. It is the server that calls it; a client never rolls for weather.
 */
export function pickWindTarget(random: () => number = Math.random): number {
    return normaliseAngle(random() * Math.PI * 2);
}

/** Seconds until the wind next picks a quarter: WIND_SHIFT_INTERVAL, jittered either way. */
export function nextWindShiftDelay(random: () => number = Math.random): number {
    return WIND_SHIFT_INTERVAL * (1 - WIND_SHIFT_JITTER + random() * 2 * WIND_SHIFT_JITTER);
}
